import { useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import useModalClose from 'hooks/useModalClose'
import { CircleButton } from 'pages/Shifts/Shifts.styled'
import { CursorPointer, Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Card, CardBody } from 'components/Card/Card'
import { IFoodKitchen } from 'types/foodLogistics/foodKitchen.interface'
import { IFoodMealRequest, IMealRequestLine, MealRequestType } from 'types/foodLogistics/foodMealRequest.interface'
import { IMealWindow } from 'types/foodLogistics/mealWindow.interface'
import { FOOD_DIETARY_TAGS } from '../foodData'

interface IProps {
  kitchens: IFoodKitchen[]
  windows: IMealWindow[]
  existingCount: number
  closeModal: () => void
  onSubmit: (request: IFoodMealRequest) => void
}

interface IRequestLineDraft {
  tag: string
  qty: string
}

export const CreateMealRequestModal = ({ kitchens, windows, existingCount, closeModal, onSubmit }: IProps) => {
  const [type, setType] = useState<MealRequestType>('Guest')
  const [day, setDay] = useState<Date>(new Date('2028-02-20'))
  const [meal, setMeal] = useState(windows[0]?.name || 'Breakfast')
  const [lines, setLines] = useState<IRequestLineDraft[]>([{ tag: 'Regular', qty: '1' }])
  const [kitchenPref, setKitchenPref] = useState<string>('')
  const [reason, setReason] = useState('')
  useModalClose(closeModal)

  const total = lines.reduce((s, l) => s + Number(l.qty || 0), 0)
  const valid = reason.trim() && total > 0

  const tagOptions = FOOD_DIETARY_TAGS.map((t) => ({ label: t, value: t }))
  const mealOptions = windows.map((w) => ({ label: w.name, value: w.name }))
  const kitchenOptions = [{ label: 'No preference', value: '' }, ...kitchens.map((k) => ({ label: k.name, value: k.name }))]

  const handleSubmit = () => {
    const id = 'REQ-00' + (existingCount + 1)
    const parsedLines: IMealRequestLine[] = lines.map((l) => ({ tags: [l.tag], qty: Number(l.qty) }))
    onSubmit({
      id,
      type,
      meal,
      day: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
      qty: total,
      lines: parsedLines,
      routing: '—',
      status: 'Pending',
      by: 'Mohamed Diara',
      reason,
    })
  }

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Create Meal Request</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Flex gap='0.5rem' width='100%'>
          {(['Guest', 'Emergency'] as MealRequestType[]).map((t) => (
            <Card
              key={t}
              onClick={() => setType(t)}
              fullBorder={type === t ? colors.orange : undefined}
              hoverBorder
              noScale
            >
              <CardBody>
                <Flex gap='0.5rem' center>
                  <CircleButton checked={type === t} />
                  <Flex column gap='0.125rem'>
                    <BodyText weight='700'>{t} Meal</BodyText>
                    <OtherText grey>
                      {t === 'Guest' ? 'Planned visitor or contractor' : 'Unexpected operational need'}
                    </OtherText>
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </Flex>
        <Flex gap='0.75rem' width='100%'>
          <CalendarInput
            labelText='Operational day'
            placeholder='Select date'
            defaultValue={day}
            onChange={(d: Date) => setDay(d)}
            width='100%'
            nomarg
          />
          <HonestSelectInput
            width='100%'
            labelText='Meal window'
            options={mealOptions}
            defaultValue={meal}
            onChange={(v: any) => setMeal(v.value)}
            name='meal'
            nomarg
          />
        </Flex>
        <BodyText weight='600'>Dietary breakdown</BodyText>
        {lines.map((l, i) => (
          <Flex gap='0.5rem' width='100%' alignEnd key={i}>
            <HonestSelectInput
              width='100%'
              labelText='Dietary'
              options={tagOptions}
              defaultValue={l.tag}
              onChange={(v: any) => setLines(lines.map((x, j) => (j === i ? { ...x, tag: v.value } : x)))}
              name='tag'
              nomarg
            />
            <Input
              type='number'
              labelText='Quantity'
              placeholder='Enter quantity'
              value={l.qty}
              onChange={(e: any) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
              nomarg
            />
            {lines.length > 1 && (
              <CursorPointer onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                <CloseIcon />
              </CursorPointer>
            )}
          </Flex>
        ))}
        <Button variant='secondary' size='sm' onClick={() => setLines([...lines, { tag: 'Regular', qty: '1' }])}>
          Add dietary line
        </Button>
        <Flex gap='0.75rem' width='100%'>
          <HonestSelectInput
            width='100%'
            labelText='Kitchen preference (optional)'
            options={kitchenOptions}
            defaultValue={kitchenPref}
            onChange={(v: any) => setKitchenPref(v.value)}
            name='kitchenPref'
            nomarg
          />
          <Flex column gap='0.25rem' width='100%'>
            <OtherText grey>Total quantity</OtherText>
            <BodyText weight='600'>{total}</BodyText>
          </Flex>
        </Flex>
        <Input
          type='textarea'
          labelText='Reason'
          required
          value={reason}
          onChange={(e: any) => setReason(e.target.value)}
          placeholder='Please provide a reason…'
          nomarg
        />
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button variant='secondary' onClick={closeModal}>Cancel</Button>
          <Button variant='primary' disabled={!valid} onClick={handleSubmit}>
            Submit
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
